import { useState } from 'react'

export default function ContactFormSection({ title, subtitle }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error'

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes'
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig emailadresse'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse skal udfyldes'
    }
    return newErrors
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        setSubmitStatus('error')
      }
    } catch (err) {
      console.error('Submit error:', err)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white border-t border-warm-gray-200 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-0 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Info */}
          <div className="lg:col-span-5 space-y-6 px-4 sm:px-0">
            <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite">
              {title}
            </h3>
            <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed">
              {subtitle}
            </p>

            <div className="pt-4 divide-y divide-warm-gray-200">
              <div className="flex items-center gap-4 text-warm-gray-700 py-4 first:pt-0 last:pb-0">
                <div className="text-black text-lg flex items-center justify-center shrink-0 w-6">
                  <i className="fa-light fa-phone" />
                </div>
                <div>
                  <a href="tel:+4528558925" className="text-sm font-semibold hover:text-brand-green transition-colors">
                    28 55 89 25
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 text-warm-gray-700 py-4 first:pt-0 last:pb-0">
                <div className="text-black text-lg flex items-center justify-center shrink-0 w-6">
                  <i className="fa-light fa-envelope" />
                </div>
                <div>
                  <a href="mailto:kontakt@k-bm.dk" className="text-sm font-semibold hover:text-brand-green transition-colors break-all">
                    kontakt@k-bm.dk
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 text-warm-gray-700 py-4 first:pt-0 last:pb-0">
                <div className="text-black text-lg flex items-center justify-center shrink-0 w-6">
                  <i className="fa-light fa-location-dot" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Bygade 51E, 7173 Vonge</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-none sm:rounded-lg border-y sm:border border-warm-gray-200 shadow-sm">
            <h4 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-2">Få et uforpligtende tilbud</h4>
            <p className="text-xs sm:text-sm text-warm-gray-500 mb-6">
              Udfyld formularen, så vender jeg tilbage til dig hurtigst muligt.
            </p>

            {submitStatus === 'success' && (
              <div className="mb-6 rounded bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 flex items-start gap-3">
                <i className="fa-light fa-circle-check text-xl text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h5 className="font-bold uppercase tracking-wider text-sm">Beskeden blev sendt!</h5>
                  <p className="text-xs sm:text-sm mt-1">Tak for din henvendelse. Jeg vender tilbage til dig så hurtigt som muligt.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 rounded bg-red-50 border border-red-200 p-4 text-red-800 flex items-start gap-3">
                <i className="fa-light fa-circle-exclamation text-xl text-red-600 mt-0.5 shrink-0" />
                <div>
                  <h5 className="font-bold uppercase tracking-wider text-sm">Der skete en fejl</h5>
                  <p className="text-xs sm:text-sm mt-1">Kunne ikke sende din besked. Prøv venligst igen eller ring til mig direkte.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot Field - absolutely hidden from humans */}
              <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
                <label htmlFor="website_url">Honeypot</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                  Navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full rounded border px-4 py-2.5 text-sm text-anthracite focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-all ${
                    errors.name ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200 bg-warm-gray-50/50'
                  }`}
                  placeholder="Dit fulde navn"
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>

              {/* Phone and Email Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Phone Field */}
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full rounded border px-4 py-2.5 text-sm text-anthracite focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-all ${
                      errors.phone ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200 bg-warm-gray-50/50'
                    }`}
                    placeholder="F.eks. 28558925"
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded border px-4 py-2.5 text-sm text-anthracite focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-all ${
                      errors.email ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200 bg-warm-gray-50/50'
                    }`}
                    placeholder="din@email.dk"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>

              {/* Description Field */}
              <div>
                <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                  Beskrivelse <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  className={`w-full rounded border px-4 py-2.5 text-sm text-anthracite focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-all resize-none ${
                    errors.description ? 'border-red-500 bg-red-50/10' : 'border-warm-gray-200 bg-warm-gray-50/50'
                  }`}
                  placeholder="Beskriv dit projekt, dine ønsker og eventuelle tidsrammer..."
                />
                {errors.description && <p className="mt-1 text-xs text-red-500">{errors.description}</p>}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded bg-brand-green py-3 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      sender besked...
                    </>
                  ) : (
                    <>
                      <i className="fa-light fa-paper-plane" />
                      send besked
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
